import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { astrologyChartAPI } from '../services/api'

export interface Contact {
  id: string
  name: string
  relationship: string
  category?: 'family' | 'friends' | 'business'
  birthData?: {
    day: number
    month: number
    year: number
    hour?: number
    minute?: number
    latitude?: number
    longitude?: number
    timezone?: number
    place_of_birth?: string
  }
  chartStatus?: 'pending' | 'importing' | 'completed' | 'failed'
  chartData?: any
  createdAt: Date
}

export interface User {
  id: string
  uid: string
  name: string
  email: string
  birthData?: {
    name: string
    day: number
    month: number
    year: number
    hour?: number
    minute?: number
    latitude?: number
    longitude?: number
    place_of_birth?: string
  }
  contacts?: Contact[]
  astroProfile?: any
  doshaProfile?: any
  preferences?: any
  createdAt: Date
  updatedAt: Date
}

interface UserStore {
  user: User | null
  isLoading: boolean
  setUser: (user: User) => void
  updateUser: (updates: Partial<User>) => void
  setBirthData: (birthData: User['birthData']) => void
  setAstroProfile: (profile: any) => void
  setDoshaProfile: (profile: any) => void
  addContact: (contact: Omit<Contact, 'id' | 'createdAt'>) => void
  updateContact: (contactId: string, updates: Partial<Contact>) => void
  removeContact: (contactId: string) => void
  importContactCharts: (userId: string, contactId: string, contactName: string, birthData: any) => Promise<void>
  retryContactChartImport: (contactId: string) => Promise<void>
  importPendingContactCharts: () => Promise<void>
  syncWithAuth: (authUser: any) => void
  logout: () => void
  setLoading: (loading: boolean) => void
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      
      setUser: (user) => {
        console.log('Setting user in userStore:', user);
        set({ user });
      },
      
      updateUser: (updates) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null
      })),
      
      setBirthData: (birthData) => set((state) => ({
        user: state.user ? { ...state.user, birthData } : null
      })),
      
      setAstroProfile: (astroProfile) => set((state) => ({
        user: state.user ? { ...state.user, astroProfile } : null
      })),
      
      setDoshaProfile: (doshaProfile) => set((state) => ({
        user: state.user ? { ...state.user, doshaProfile } : null
      })),
      
      addContact: (contactData) => set((state) => {
        if (!state.user) {
          console.error('Cannot add contact: no user in userStore');
          return state;
        }
        
        const newContact: Contact = {
          ...contactData,
          id: `contact_${Date.now()}`,
          chartStatus: 'pending',
          createdAt: new Date()
        }
        
        const contacts = state.user.contacts || []
        
        // Start chart import process asynchronously
        if (contactData.birthData && state.user?.uid) {
          // Use setTimeout to avoid blocking the UI update
          setTimeout(() => {
            const store = useUserStore.getState();
            const currentUser = store.user;
            if (currentUser?.uid) {
              store.importContactCharts(currentUser.uid, newContact.id, newContact.name, contactData.birthData);
            } else {
              console.error('Cannot import contact charts: user not logged in or missing UID');
            }
          }, 0);
        }
        
        return {
          user: {
            ...state.user,
            contacts: [...contacts, newContact]
          }
        }
      }),
      
      updateContact: (contactId, updates) => set((state) => {
        if (!state.user?.contacts) return state
        
        const contacts = state.user.contacts.map(contact =>
          contact.id === contactId ? { ...contact, ...updates } : contact
        )
        
        return {
          user: {
            ...state.user,
            contacts
          }
        }
      }),
      
      removeContact: (contactId) => set((state) => {
        if (!state.user?.contacts) return state
        
        const contacts = state.user.contacts.filter(contact => contact.id !== contactId)
        
        return {
          user: {
            ...state.user,
            contacts
          }
        }
      }),
      
      // Import contact charts
      importContactCharts: async (userId: string, contactId: string, contactName: string, birthData: any) => {
        console.log('Starting contact chart import:', { userId, contactId, contactName, birthData });
        console.log('User ID type:', typeof userId, 'User ID value:', userId);
        console.log('Contact ID type:', typeof contactId, 'Contact ID value:', contactId);
        
        if (!userId) {
          console.error('Cannot import contact charts: userId is undefined or null');
          return;
        }
        
        // Ensure birthData has the name field
        const completeBirthData = {
          ...birthData,
          name: contactName // Add the name field from contactName
        };
        
        console.log('Complete birthData:', completeBirthData);
        
        try {
          // Update contact status to importing
          set((state) => {
            if (!state.user?.contacts) return state
            
            const contacts = state.user.contacts.map(contact =>
              contact.id === contactId 
                ? { ...contact, chartStatus: 'importing' as const }
                : contact
            )
            
            return {
              user: {
                ...state.user,
                contacts
              }
            }
          })
          
          // Try import with retry mechanism (2 attempts)
          let result = null;
          let attempt = 1;
          const maxAttempts = 2;
          
          while (attempt <= maxAttempts) {
            try {
              console.log(`Attempt ${attempt} of ${maxAttempts} for contact chart import:`, contactName);
              
              // Import charts
              result = await astrologyChartAPI.importContactCharts(userId, contactId, contactName, completeBirthData);
              
              if (result.success) {
                console.log('Contact chart import successful:', contactName, result);
                break; // Success, exit retry loop
              } else {
                console.error(`Attempt ${attempt} failed:`, result.error);
                if (attempt === maxAttempts) {
                  throw new Error(result.error || 'Failed to import contact charts');
                }
              }
            } catch (error) {
              console.error(`Attempt ${attempt} error:`, error);
              if (attempt === maxAttempts) {
                throw error;
              }
            }
            attempt++;
            
            // Wait 1 second before retry
            if (attempt <= maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
          
          if (result && result.success) {
            // Update contact status to completed
            set((state) => {
              if (!state.user?.contacts) return state
              
              const contacts = state.user.contacts.map(contact =>
                contact.id === contactId 
                  ? { 
                      ...contact, 
                      chartStatus: 'completed' as const,
                      chartData: result.charts
                    }
                  : contact
              )
              
              return {
                user: {
                  ...state.user,
                  contacts
                }
              }
            })
            
            console.log(`Charts imported successfully for contact: ${contactName}`)
          } else {
            throw new Error('Failed to import contact charts after all attempts')
          }
        } catch (error) {
          console.error('Error importing contact charts:', error)
          
          // Update contact status to failed
          set((state) => {
            if (!state.user?.contacts) return state
            
            const contacts = state.user.contacts.map(contact =>
              contact.id === contactId 
                ? { ...contact, chartStatus: 'failed' as const }
                : contact
            )
            
            return {
              user: {
                ...state.user,
                contacts
              }
            }
          })
        }
      },
      
      retryContactChartImport: async (contactId: string) => {
        const currentUser = useUserStore.getState().user;
        if (!currentUser?.uid) {
          console.error('Cannot retry chart import: user not logged in or missing UID');
          return;
        }

        const contact = currentUser.contacts?.find(c => c.id === contactId);
        if (!contact || contact.chartStatus !== 'failed') {
          console.warn(`Attempted to retry import for contact ${contactId}, but it's not in failed status.`);
          return;
        }

        if (!contact.birthData || !contact.id) {
          console.error(`Cannot retry import for contact ${contactId}: missing birthData or ID.`);
          return;
        }

        console.log(`Retrying chart import for contact: ${contact.name} (ID: ${contact.id})`);
        try {
          // Ensure birthData has the name field
          const completeBirthData = {
            ...contact.birthData,
            name: contact.name // Add the name field from contact name
          };
          
          await useUserStore.getState().importContactCharts(
            currentUser.uid,
            contact.id,
            contact.name,
            completeBirthData
          );
          console.log(`Chart import for contact ${contact.name} (ID: ${contact.id}) retried successfully.`);
        } catch (error) {
          console.error(`Failed to retry chart import for contact ${contact.name} (ID: ${contact.id}):`, error);
        }
      },

      importPendingContactCharts: async () => {
        const currentUser = useUserStore.getState().user;
        console.log('importPendingContactCharts called, currentUser:', currentUser);
        
        if (!currentUser?.uid) {
          console.error('Cannot import pending charts: user not logged in or missing UID');
          console.log('User object:', currentUser);
          return;
        }

        const contacts = currentUser.contacts?.filter(
          (contact) => contact.chartStatus === 'pending'
        );
        console.log('Pending contacts found:', contacts?.length || 0);
        
        if (!contacts || contacts.length === 0) {
          console.log('No pending contact charts to import.');
          return;
        }

        console.log(`Attempting to import ${contacts.length} pending contact charts...`);
        for (const contact of contacts) {
          console.log('Processing contact:', contact.name, 'ID:', contact.id, 'Status:', contact.chartStatus);
          if (contact.birthData && contact.id) {
            try {
              console.log('Calling importContactCharts with:', {
                userId: currentUser.uid,
                contactId: contact.id,
                contactName: contact.name,
                birthData: contact.birthData
              });
              
              // Ensure birthData has the name field
              const completeBirthData = {
                ...contact.birthData,
                name: contact.name // Add the name field from contact name
              };
              
              await useUserStore.getState().importContactCharts(
                currentUser.uid,
                contact.id,
                contact.name,
                completeBirthData
              );
              console.log(`Imported pending chart for contact: ${contact.name} (ID: ${contact.id})`);
            } catch (error) {
              console.error(`Failed to import pending chart for contact ${contact.name} (ID: ${contact.id}):`, error);
            }
          } else {
            console.warn(`Skipping pending contact ${contact.name} (ID: ${contact.id}) due to missing birthData or ID.`);
          }
        }
        console.log(`Finished importing ${contacts.length} pending contact charts.`);
      },
      
      syncWithAuth: (authUser: any) => {
        console.log('Syncing userStore with authUser:', authUser);
        if (authUser) {
          // Merge authUser with existing userStore data
          const currentUser = get().user;
          const mergedUser = {
            ...currentUser,
            ...authUser,
            uid: authUser.uid || authUser.id,
            contacts: currentUser?.contacts || []
          };
          console.log('Merged user data:', mergedUser);
          set({ user: mergedUser });
        } else {
          set({ user: null });
        }
      },
      
      logout: () => set({ user: null }),
      
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'aum-user-storage',
    }
  )
) 